/**
 * SchematicViewer
 *
 * SVG renderer for schematic visual artifacts.
 *
 * Primary responsibilities:
 * - Render nodes and directed edges from a schematic artifact
 * - Provide a compact, canvas-safe diagram surface
 *
 * Dependencies:
 * - Visual artifact types
 */

'use client'

import type { SchematicArtifact, SchematicNode } from '@/lib/visual-artifacts'

type SchematicViewerProps = {
  artifact: SchematicArtifact
  className?: string
}

type Point = {
  x: number
  y: number
}

const nodeWidth = 184
const nodeHeight = 68
const padding = 52
const columnGap = 96
const rowGap = 92
const minimumCanvasWidth = 640
const minimumCanvasHeight = 320

export function SchematicViewer({ artifact, className }: SchematicViewerProps) {
  const layout = getNodeLayout(artifact.data.nodes)
  const bounds = getBounds(layout)
  const width = Math.max(bounds.maxX + padding, minimumCanvasWidth)
  const height = Math.max(bounds.maxY + padding, minimumCanvasHeight)

  return (
    <section
      className={[
        'not-prose overflow-hidden rounded-lg border bg-background text-foreground shadow-sm',
        className
      ].filter(Boolean).join(' ')}
      aria-label={artifact.title}
    >
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{artifact.title}</h3>
          <p className="text-muted-foreground text-xs">
            {artifact.data.nodes.length} nodes / {artifact.data.edges.length} links
          </p>
        </div>
      </div>

      <div className="overflow-auto bg-slate-50">
        <svg
          className="block"
          style={{
            width,
            height,
            minWidth: '100%'
          }}
          role="img"
          aria-label={`${artifact.title} schematic`}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMinYMin meet"
        >
          <defs>
            <marker
              id={`${artifact.id}-arrow`}
              markerHeight="8"
              markerWidth="8"
              orient="auto"
              refX="7"
              refY="4"
            >
              <path d="M 0 0 L 8 4 L 0 8 z" fill="#64748b" />
            </marker>
          </defs>

          <rect width={width} height={height} fill="#f8fafc" />
          {artifact.data.edges.map((edge) => {
            const from = layout.get(edge.from)
            const to = layout.get(edge.to)

            if (!from || !to) return null

            const start = {
              x: from.x + nodeWidth,
              y: from.y + nodeHeight / 2
            }
            const end = {
              x: to.x,
              y: to.y + nodeHeight / 2
            }
            const midX = (start.x + end.x) / 2
            const path = `M ${start.x} ${start.y} C ${midX} ${start.y}, ${midX} ${end.y}, ${end.x} ${end.y}`

            return (
              <g key={edge.id}>
                <path
                  d={path}
                  fill="none"
                  markerEnd={`url(#${artifact.id}-arrow)`}
                  stroke="#64748b"
                  strokeWidth="2"
                />
                {edge.label && (
                  <text
                    x={midX}
                    y={(start.y + end.y) / 2 - 8}
                    fill="#475569"
                    fontSize="11"
                    fontWeight="600"
                    paintOrder="stroke"
                    stroke="#f8fafc"
                    strokeWidth="4"
                    textAnchor="middle"
                  >
                    {truncate(edge.label, 20)}
                  </text>
                )}
              </g>
            )
          })}

          {artifact.data.nodes.map((node) => {
            const point = layout.get(node.id)

            if (!point) return null

            const labelLines = wrapText(node.label, 22, 2)

            return (
              <g key={node.id} transform={`translate(${point.x} ${point.y})`}>
                <rect
                  width={nodeWidth}
                  height={nodeHeight}
                  rx="10"
                  fill="#ffffff"
                  stroke={getNodeStroke(node.kind)}
                  strokeWidth="2"
                />
                <text x="16" y="24" fill="#0f172a" fontSize="12.5" fontWeight="700">
                  {labelLines.map((line, index) => (
                    <tspan key={`${node.id}-label-${index}`} x="16" dy={index === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))}
                </text>
                {node.kind && (
                  <text x="16" y="55" fill="#64748b" fontSize="10" fontWeight="600">
                    {truncate(node.kind, 26)}
                  </text>
                )}
              </g>
            )
          })}
        </svg>
      </div>
    </section>
  )
}

function getNodeLayout(nodes: SchematicNode[]) {
  const layout = new Map<string, Point>()
  const missingPositions = nodes.filter(
    (node) => typeof node.x !== 'number' || typeof node.y !== 'number'
  )
  const hasCustomPositions = missingPositions.length !== nodes.length
  const columns = Math.max(Math.ceil(Math.sqrt(nodes.length)), 1)

  nodes.forEach((node, index) => {
    if (hasCustomPositions && typeof node.x === 'number' && typeof node.y === 'number') {
      layout.set(node.id, {
        x: padding + node.x,
        y: padding + node.y
      })
      return
    }

    const column = index % columns
    const row = Math.floor(index / columns)

    layout.set(node.id, {
      x: padding + column * (nodeWidth + columnGap),
      y: padding + row * (nodeHeight + rowGap)
    })
  })

  return layout
}

function getBounds(layout: Map<string, Point>) {
  const points = Array.from(layout.values())

  if (points.length === 0) {
    return {
      maxX: 0,
      maxY: 0
    }
  }

  return {
    maxX: Math.max(...points.map((point) => point.x + nodeWidth)),
    maxY: Math.max(...points.map((point) => point.y + nodeHeight))
  }
}

function getNodeStroke(kind: string | undefined) {
  if (!kind) return '#cbd5e1'

  const normalized = kind.toLowerCase()

  if (normalized.includes('decision')) return '#d97706'
  if (normalized.includes('entry') || normalized.includes('start')) return '#0f766e'
  if (normalized.includes('output') || normalized.includes('end')) return '#2563eb'

  return '#64748b'
}

function truncate(value: string, maxLength: number) {
  if (value.length <= maxLength) return value
  return `${value.slice(0, Math.max(maxLength - 3, 0))}...`
}

function wrapText(value: string, maxLineLength: number, maxLines: number) {
  const words = value.split(/\s+/).filter(Boolean)
  const lines: string[] = []

  words.forEach((word) => {
    const currentLine = lines[lines.length - 1]

    if (!currentLine || `${currentLine} ${word}`.length > maxLineLength) {
      lines.push(truncate(word, maxLineLength))
      return
    }

    lines[lines.length - 1] = `${currentLine} ${word}`
  })

  if (lines.length <= maxLines) {
    return lines
  }

  return [
    ...lines.slice(0, maxLines - 1),
    truncate(lines.slice(maxLines - 1).join(' '), maxLineLength)
  ]
}
